/**
 * Errors of the assistant connection layer.
 *
 * A server rejection is `AiContractError`, built defensively from whatever
 * body arrived: a missing `details`, a non-string code or a body that is not
 * an envelope at all must never break the UI. The UI switches on
 * `detailCode` only for the codes in `CONFIRMED_ERROR_CODES`, shows
 * `displayMessage`, and shows `requestId` for support.
 */
import { AI_ERROR_MESSAGES, type AiErrorDetail } from './contract.ts';

export const FORBIDDEN_MESSAGE = 'دسترسی شما برای این کار کافی نیست.';
export const GENERIC_ERROR_MESSAGE = 'خطایی رخ داد. لطفاً دوباره تلاش کنید.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export class AiContractError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: readonly AiErrorDetail[];
  readonly requestId: string;

  constructor(
    status: number,
    code: string,
    details: readonly AiErrorDetail[],
    requestId: string,
    message?: string,
  ) {
    super(message ?? details[0]?.message ?? GENERIC_ERROR_MESSAGE);
    this.name = 'AiContractError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Builds an error from an arbitrary response body; never throws. */
  static fromBody(status: number, body: unknown): AiContractError {
    const error = isRecord(body) && isRecord(body['error']) ? body['error'] : null;
    const code = typeof error?.['code'] === 'string' ? error['code'] : 'UNKNOWN';
    const requestId = typeof error?.['requestId'] === 'string' ? error['requestId'] : '';
    const rawDetails = Array.isArray(error?.['details']) ? (error['details'] as unknown[]) : [];
    const details = rawDetails.flatMap((entry): AiErrorDetail[] => {
      if (!isRecord(entry)) return [];
      if (typeof entry['code'] !== 'string' || typeof entry['message'] !== 'string') return [];
      return [
        {
          path: typeof entry['path'] === 'string' ? entry['path'] : '',
          code: entry['code'],
          message: entry['message'],
        },
      ];
    });
    return new AiContractError(status, code, details, requestId);
  }

  /** `error.details[0].code`, the code the UI branches on. */
  get detailCode(): string | undefined {
    return this.details[0]?.code;
  }

  /** True when the server gave no usable message and the generic text is shown. */
  get isGeneric(): boolean {
    const message = this.details[0]?.message;
    if (typeof message === 'string' && message.trim() !== '') return false;
    return (
      this.status !== 403 &&
      (this.detailCode === undefined || AI_ERROR_MESSAGES[this.detailCode] === undefined)
    );
  }

  /** `error.details[0].message`, else the handoff's text for a confirmed code, else generic. */
  get displayMessage(): string {
    const message = this.details[0]?.message;
    if (typeof message === 'string' && message.trim() !== '') return message;
    const known = this.detailCode === undefined ? undefined : AI_ERROR_MESSAGES[this.detailCode];
    if (known !== undefined) return known;
    // Missing permission is a plain 403 FORBIDDEN without details: say so, not "an error occurred".
    return this.status === 403 ? FORBIDDEN_MESSAGE : GENERIC_ERROR_MESSAGE;
  }

  /** 401: the session is gone; the page layer sends the user to login. */
  get isSessionExpired(): boolean {
    return this.status === 401;
  }
}

/** The caller (or `dispose()`) cancelled the wait. This does NOT cancel the server run. */
export class AiAbortedError extends Error {
  /** The request may have reached the server; read the real state before assuming anything. */
  readonly outcomeUnknown: boolean;
  constructor(outcomeUnknown: boolean) {
    super('انتظار پاسخ لغو شد.');
    this.name = 'AiAbortedError';
    this.outcomeUnknown = outcomeUnknown;
  }
}

/** No response within the deadline. The server may still finish the call. */
export class AiTimeoutError extends Error {
  readonly outcomeUnknown: boolean;
  constructor(outcomeUnknown: boolean) {
    super('پاسخی از سرور دریافت نشد.');
    this.name = 'AiTimeoutError';
    this.outcomeUnknown = outcomeUnknown;
  }
}

/** The request never completed (offline, DNS, reset). */
export class AiNetworkError extends Error {
  readonly outcomeUnknown: boolean;
  constructor(outcomeUnknown: boolean) {
    super('ارتباط با سرور برقرار نشد.');
    this.name = 'AiNetworkError';
    this.outcomeUnknown = outcomeUnknown;
  }
}

/** A 2xx body that does not match the contract. Nothing from it is shown. */
export class AiInvalidResponseError extends Error {
  readonly path: string;
  constructor(path: string) {
    super('پاسخ سرور معتبر نیست.');
    this.name = 'AiInvalidResponseError';
    this.path = path;
  }
}

export interface DescribedError {
  readonly message: string;
  /** Empty when the server sent none. */
  readonly requestId: string;
  readonly generic: boolean;
}

/** One place that turns any thrown value into text for the UI. */
export function describeError(error: unknown): DescribedError {
  if (error instanceof AiContractError) {
    return {
      message: error.displayMessage,
      requestId: error.requestId,
      generic: error.isGeneric,
    };
  }
  if (
    error instanceof AiAbortedError ||
    error instanceof AiTimeoutError ||
    error instanceof AiNetworkError ||
    error instanceof AiInvalidResponseError
  ) {
    return { message: error.message, requestId: '', generic: false };
  }
  return { message: GENERIC_ERROR_MESSAGE, requestId: '', generic: true };
}
